/**
 * 약관 화면 공용 유틸.
 *
 * 약관 API의 일시는 KST `yyyy-MM-dd HH:mm:ss` 문자열이다. 공지처럼 +9시간 ISO 변환을
 * 하면 시행일시가 어긋나므로, 입력한 날짜·시각을 그대로 조립해서 보낸다.
 */

/** DatePicker(`yyyy-MM-dd`) + TimePicker(`HH:mm`) → 서버 일시 문자열 */
export const toServerDateTime = (date: string, time: string): string => {
  if (!date) return '';
  const safeTime = /^\d{2}:\d{2}$/.test(time) ? time : '00:00';
  return `${date} ${safeTime}:00`;
};

/** 즉시 시행용 기본값 — 오늘 날짜와 현재 시각(분 단위) */
export const getNowDateTimeParts = (): { date: string; time: string } => {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return {
    date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    time: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
  };
};

/**
 * 에디터 본문이 사실상 비었는지 판단한다.
 * 서버도 sanitize 후 빈 본문을 400으로 막지만, 저장 전에 먼저 걸러 안내한다.
 */
export const isTermsContentEmpty = (html: string): boolean => {
  if (!html) return true;
  const text = html
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .trim();
  return text.length === 0;
};

/**
 * 약관 일시(KST, 오프셋 없음)를 epoch ms로 바꾼다.
 * 브라우저 타임존에 휘둘리지 않게 KST(+09:00)로 해석한다.
 */
export const parseTermsDateTime = (value?: string | null): number | null => {
  if (!value) return null;
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value);
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const target = new Date(hasZone ? normalized : `${normalized}+09:00`);
  const time = target.getTime();
  return Number.isNaN(time) ? null : time;
};

/** 시행 예정 — 시행일시가 아직 오지 않은 버전 */
export const isScheduled = (effectiveAt?: string | null): boolean => {
  const start = parseTermsDateTime(effectiveAt);
  return start != null && start > Date.now();
};

/**
 * 지금 이 시각에 실제로 적용 중인 버전인지.
 * 시행일시가 지났고, 다음 버전 시행일시(endedAt)가 없거나 아직 오지 않아야 한다.
 */
export const isCurrentlyEffective = (
  effectiveAt?: string | null,
  endedAt?: string | null,
): boolean => {
  const start = parseTermsDateTime(effectiveAt);
  if (start == null || start > Date.now()) return false;
  const end = parseTermsDateTime(endedAt);
  return end == null || end > Date.now();
};
